const NON_ENUM_SOURCE_DATATYPES = new Set([
  "number",
  "integer",
  "decimal",
  "float",
  "double",
]);

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is string =>
      typeof item === "string" && item.trim().length > 0,
  );
}

function isNonEnumSourceDatatype(raw: Record<string, unknown>): boolean {
  const datatype = raw.datatype;
  if (typeof datatype !== "string") return false;
  return NON_ENUM_SOURCE_DATATYPES.has(datatype.toLowerCase());
}

function isBooleanSourceDatatype(raw: Record<string, unknown>): boolean {
  const datatype = raw.datatype;
  return typeof datatype === "string" && datatype.toLowerCase() === "boolean";
}

function extractFromSchemaFragment(fragment: unknown): string[] {
  if (!fragment || typeof fragment !== "object") return [];

  const record = fragment as Record<string, unknown>;
  const direct = asStringList(
    record.enums ?? record.enum ?? record.enum_values,
  );
  if (direct.length > 0) return direct;

  const candidateKeys = [
    "items",
    "values",
    "properties",
    "oneOf",
    "anyOf",
    "allOf",
    "children",
    "constraints",
    "validation",
  ];

  for (const key of candidateKeys) {
    const nested = record[key];
    if (Array.isArray(nested)) {
      for (const entry of nested) {
        const extracted = extractFromSchemaFragment(entry);
        if (extracted.length > 0) return extracted;
      }
      continue;
    }

    const extracted = extractFromSchemaFragment(nested);
    if (extracted.length > 0) return extracted;
  }

  for (const value of Object.values(record)) {
    const extracted = extractFromSchemaFragment(value);
    if (extracted.length > 0) return extracted;
  }

  return [];
}

export function extractSourceEnumValues(
  sourceEnumValues: unknown,
  marketplaceRawJson: unknown,
): string[] {
  if (
    marketplaceRawJson &&
    typeof marketplaceRawJson === "object" &&
    isBooleanSourceDatatype(marketplaceRawJson as Record<string, unknown>)
  ) {
    return [];
  }

  const explicit = asStringList(sourceEnumValues);
  if (explicit.length > 0) return explicit;

  // Numeric source fields aren't enum-shaped just because the raw schema
  // carries an `enums` array. Only backend-provided sourceEnumValues above are
  // treated as authoritative (e.g. unit_count with sourceEnumValues:["count"]).
  if (
    marketplaceRawJson &&
    typeof marketplaceRawJson === "object" &&
    isNonEnumSourceDatatype(marketplaceRawJson as Record<string, unknown>)
  ) {
    return [];
  }

  if (!marketplaceRawJson || typeof marketplaceRawJson !== "object") return [];

  const raw = marketplaceRawJson as Record<string, unknown>;
  const direct = asStringList(raw.enums);
  if (direct.length > 0) return direct;

  const constraints =
    raw.constraints && typeof raw.constraints === "object"
      ? (raw.constraints as Record<string, unknown>)
      : null;
  if (constraints) {
    const constraintEnums = asStringList(
      constraints.enums ?? constraints.enum_values,
    );
    if (constraintEnums.length > 0) return constraintEnums;

    const fragmentEnums = extractFromSchemaFragment(
      constraints.source_schema_fragment,
    );
    if (fragmentEnums.length > 0) return fragmentEnums;
  }

  const validations =
    raw.marketplaceValidations && typeof raw.marketplaceValidations === "object"
      ? (raw.marketplaceValidations as Record<string, unknown>)
      : raw.marketplace_validations &&
          typeof raw.marketplace_validations === "object"
        ? (raw.marketplace_validations as Record<string, unknown>)
        : null;
  if (validations) {
    const validationEnums = asStringList(validations.enums);
    if (validationEnums.length > 0) return validationEnums;

    const validationConstraints =
      validations.constraints && typeof validations.constraints === "object"
        ? (validations.constraints as Record<string, unknown>)
        : null;
    if (validationConstraints) {
      const nestedValidationEnums = asStringList(
        validationConstraints.enums ?? validationConstraints.enum_values,
      );
      if (nestedValidationEnums.length > 0) return nestedValidationEnums;
    }
  }

  const children = Array.isArray(raw.marketplace_children)
    ? raw.marketplace_children
    : Array.isArray(raw.marketplaceChildren)
      ? raw.marketplaceChildren
      : [];
  for (const child of children) {
    const childEnums = extractSourceEnumValues([], child);
    if (childEnums.length > 0) return childEnums;
  }

  return [];
}
